import React, { PureComponent, ReactNode } from 'react';
import SectionTitle from '@flow/scenes/home/components/section-title/SectionTitle';
import styled, { keyframes, withTheme } from 'styled-components';
import em from 'polished/lib/helpers/em';
import { Link } from 'react-static';
import FlowStripe from '@flow/components/ticker/FlowStripe';
import { getRelativeUrl , getLinkTarget} from '@flow/utils/cleaners';
import { TextAnimation } from '@flow/components/animation/TextAnimation';
import { Waypoint } from 'react-waypoint';

const ImageHover = keyframes`
  0%,
  100% {
    transform: scale(1) rotate(0deg);
    filter: blur(0) grayscale(0);
  }

  50% {
    transform: scale(1.05) rotate(1deg);
    filter: blur(0.5em) grayscale(100%);
  }
`;

const GridWrapper = styled.div`
  border-top: 0.2em solid ${props => props.theme.highlightColor};
  border-bottom: 0.2em solid ${props => props.theme.highlightColor};
  
  &.with-top-spacing {
    margin-top: 10em;
    
    @media (max-width: 1920px) {
      margin-top: ${100 / (1920 * 0.01)}vw;
    }
  }
  
  @media (max-width: 760px) {
    border-bottom: none;
  }
`;

const Figure = styled.figure`
  background-image: url(${props => props.theme.isRetina ? props.image['large'] : props.image['medium']});
  background-size: cover;
`;

const Column = styled.div`
  display: flex;
  width: ${props => 100 / props.columnsCount}%;
  box-sizing: border-box;
  border-right: 0.2em solid ${props => props.theme.highlightColor};
  
  &:last-child {
    border-right: none;
  }
  
  &.layout-split {
    ${Figure} {
      padding-top: 86.94%; 
    }
  }
  
  &.layout-stacked {
    ${Figure} {
      padding-top: 55.46%;
       
    }
    
    a {
      flex-direction: column;
    }
  }
  
  a {
    position: relative;
    display: flex;
    width: 100%; 
    box-sizing: border-box;
    text-align: center;
  }
  
  &:${props => props.theme.hoverProperty} {
    a {
      background-color: ${props => props.theme.highlightColor};
    }
  }
`;

const RowContent = styled.div`
  display: flex;
`;

const StripeWrapper = styled.div`
  border-top: 0.2em solid ${props => props.theme.highlightColor};
  border-bottom: 0.2em solid ${props => props.theme.highlightColor};
  overflow: hidden;
`;

const ImageWrapper = styled.div`
  overflow: hidden;
  ${Column}.layout-split & {
    width: 50%;
  }
  
  ${Column}.layout-stacked & {
   width: 100%;
  }
`;

const ColumnContent = styled.div`
  width: 50%;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  display: flex;
 
  ${Column}.layout-stacked & {
    width: 100%;
    padding-left: 3em;
    padding-right: 3em;
    margin-top: 7em;
  }
`;

const ColumnContentTitle = styled.h1`
  ${Column}.layout-split & {
    font-family: Flow-Inverted;
  }
  font-size: ${props => props.size}em;
  white-space: pre-line;
  text-transform: uppercase;
  
  @media (max-width: 1920px) {
    font-size: ${props => (props.size * 10) / (1920 * 0.01)}vw;
  }
  
  @media (max-width: 760px) {
    font-size: ${70 / (760 * 0.01)}vw;
  }
`;

const ColumnContentDescription = styled.p`
  font-size: 2.1em;
  line-height: 1.3;
  margin: ${em(10, 2.1)} auto 0;
  max-width: ${em(33, 2.1)};
  padding-bottom: ${em(5, 2.1)};
  transform: translateY(${em(4, 2.1)});
  opacity: 0;
  transition: all 0.6s 1s ease-out;
  
  &.animate-in {
    opacity: 1;
    transform: translateY(0);
  }
  
  &.immediate-show {
    transition: all 0s 0s;
    opacity: 1;
    transform: translateY(0);
  }
  
  @media (max-width: 1920px) {
    margin-top: ${100 / (1920 * 0.01)}vw;
    padding-bottom: ${50 / (1920 * 0.01)}vw;
  }
  
  @media (max-width: 1080px) {
    font-size: 1.8em;
  }
  
  /*@media (max-width: 1920px) {
    font-size: ${21 / (1920 * 0.01)}vw;
    margin-top: ${100 / (1920 * 0.01)}vw;
  }
  
  @media (max-width: 1300px) {
    font-size: ${21 / (1300 * 0.01)}vw;
  }
  
  @media (max-width: 760px) {
    font-size: ${21 / (760 * 0.01)}vw;
  }
  
  @media (max-width: 500px) {
    font-size: ${21 / (500 * 0.01)}vw;
  }*/
`;

const Row = styled.div`
  @media (max-width: 760px) {
    &.multi-columns {
      display: none;
    }
    
    ${Column} {
      ${ImageWrapper}, ${ColumnContent} {
        width: 100%;
      }
      
      ${Figure} {
        padding-top: 86.94%;  
      }
      
      ${ColumnContentDescription} {
        max-width: none;
        padding-bottom: 0;
      }
      
      ${ColumnContent} {
        padding: 8em 3em;
      }
    
      a {
        flex-direction: column;
      }
    } 
  }
`;

const NarrowLinkMenu = styled.ul`
  @media (min-width: 760px) {
    display: none;
  }
`;

const NarrowTextLinkItem = styled.li`
  border-bottom: 0.2em solid ${props => props.theme.highlightColor};
  
  &:last-child {
    border-bottom: none;
  }
  
  a {
    position: relative;
    display: block;
    font-size: 2.2em;
    padding: ${em(3, 2.2)} ${em(3, 2.2)};
    
    &:after {
      content: '→';
      font-size: ${em(4, 2.8)};
      margin-left: ${em(1.6, 4)};
      font-family: Flow-Text;
      display: inline-block;
    }
  }
  
  &:${props => props.theme.hoverProperty} {
    a {
      background-color: ${props => props.theme.highlightColor};
    }
  }
`;

interface Column {
  title: string;
  description?: string;
  size?: number;
  direction?: string;
  link?: {
    url?: string;
    target?: string;
  };
  image?: {
    caption?: string;
    default?: string;
    thumbnail?: string;
  };
}

interface Row {
  items: Column[];
}

interface FestivalRowProps {
  rowData: Row;
  isMobile?: boolean;
}

interface FestivalRowState {
  animateContentIn: boolean;
  canRenderContent: boolean;
}

class FestivalRow extends PureComponent<FestivalRowProps, FestivalRowState> {
  constructor(props: FestivalRowProps) {
    super(props);

    this.state = {
      animateContentIn: false,
      canRenderContent: false
    };
  }

  componentDidMount() {
    this.setState({canRenderContent: true});
  }

  private _handleWaypointEnter = (): void => {
    this.setState({animateContentIn: true});
  };

  render() {
    const { rowData } = this.props;
    const { canRenderContent } = this.state;

    return (
        <Row className={rowData.items && rowData.items.length === 1 ? 'single-column' : 'multi-columns'}>
          <RowContent>
            {Array.isArray(rowData.items) && rowData.items.map((column: Column, index: number) =>
              <Column
                key={index}
                columnsCount={rowData.items.length}
                className={'layout-' + (column.image && rowData.items.length === 1 ? 'split' : 'stacked')}
              >
                <Link to={getRelativeUrl(column.link.url)} target={getLinkTarget(column.link.target)}>
                  {column.image &&
                  <ImageWrapper>
                    <Figure image={column.image}/>
                  </ImageWrapper>
                  }
                  {canRenderContent &&
                    this._renderContent(rowData, column)
                  }
                </Link>
              </Column>
            )}
          </RowContent>
          <StripeWrapper>
            <FlowStripe animate={true} color={'#000000'} />
          </StripeWrapper>
        </Row>
    )
  }

  private _renderContent = (rowData: Row, column: Column): ReactNode => {
    const { isMobile } = this.props;
    if (!isMobile) {
      return (
        <Waypoint onEnter={this._handleWaypointEnter}>
          <ColumnContent>
            <div>
              <ColumnContentTitle size={rowData.items.length > 1 ? 5.3 : 13.3}>
                <TextAnimation text={column.title} animateIn={this.state.animateContentIn}/>
              </ColumnContentTitle>
              <ColumnContentDescription
                className={this.state.animateContentIn ? 'animate-in' : ''}>{column.description}</ColumnContentDescription>
            </div>
          </ColumnContent>
        </Waypoint>
      )
    } else {
      return (
        <ColumnContent>
          <div>
            <ColumnContentTitle size={rowData.items.length > 1 ? 5.3 : 13.3}>{column.title}</ColumnContentTitle>
            <ColumnContentDescription className="immediate-show">{column.description}</ColumnContentDescription>
          </div>
        </ColumnContent>
      )
    }
  }
}

interface TheFestival {
  title?: string;
  lift_rows?: Array<Row>;
}

interface Props {
  the_festival: TheFestival;
  theme?: any;
}

class FestivalLift extends PureComponent<Props> {

  constructor(props: Props) {
    super(props);
  }

  render() {
    const {
      the_festival,
      theme: {
        isMobile
      }
    } = this.props;
    const {
      title = '',
      lift_rows = []
    } = the_festival;

    let narrowTextLinkItems: Column[] = [];
    for (let i: number = 0; i < lift_rows.length; i++) {
      let row: Row = lift_rows[i];
      if (row.items && row.items.length > 1) {
        for (let j: number = 0; j < row.items.length; j++) {
          narrowTextLinkItems.push(row.items[j]);
        }
      }
    }

    return (
      <div>
        {Boolean(title) && <SectionTitle title={title}/>}
        <GridWrapper className={Boolean(title) ? '' : 'with-top-spacing'}>
          {Array.isArray(lift_rows) && lift_rows.map((row: Row, index: number) =>
            <FestivalRow key={index} rowData={row} isMobile={isMobile}/>
          )}
        </GridWrapper>

        <NarrowLinkMenu>
          {narrowTextLinkItems.map((column: Column, index: number) =>
            <NarrowTextLinkItem key={index}>
              <Link to={column.link.url} target={getLinkTarget(column.link.target)}>
                {column.title}
              </Link>
            </NarrowTextLinkItem>
          )}
        </NarrowLinkMenu>
      </div>
    )
  }
};

export default withTheme(FestivalLift);



// WEBPACK FOOTER //
// /Users/ek1z/dev/flowfestival-com/src/scenes/home/components/festival-lift/FestivalLift.tsx